import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';

interface SubscriptionReport {
  revenue: number;
  growthPercent: number;
  conversionRate: number;
  newSubscriptions: number;
  cancellations: number;
  trials: number;
  period: 'month' | 'year';
  periodLabel: string;
}

export default function BillingReportsPage() {
  const { data: currentCompanyId, role } = useCurrentCompany();

  const isOwner = role === 'owner';
  const companyId = currentCompanyId ?? null;

  const [reportPeriod, setReportPeriod] = useState<'month' | 'year'>('month');
  const [isGenerating, setIsGenerating] = useState(false);

  // Query for subscription report metrics
  const { data: reportData, isLoading: isLoadingReport } = useQuery<SubscriptionReport>({
    queryKey: ['billingSubscriptionReport', { companyId, period: reportPeriod }],
    enabled: !!companyId && isOwner,
    queryFn: async () => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      const now = new Date();
      let startDate: Date;
      let periodLabel: string;

      if (reportPeriod === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        periodLabel = `${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`;
      } else {
        startDate = new Date(now.getFullYear(), 0, 1);
        periodLabel = `${now.getFullYear()}`;
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = now.toISOString().split('T')[0];

      // Get previous period for growth calculation
      let previousStartDate: Date;
      if (reportPeriod === 'month') {
        previousStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      } else {
        previousStartDate = new Date(now.getFullYear() - 1, 0, 1);
      }
      const previousEndDate = startDate;
      const previousStartDateStr = previousStartDate.toISOString().split('T')[0];
      const previousEndDateStr = previousEndDate.toISOString().split('T')[0];

      // Get enrollment IDs for this company (payment_records doesn't have company_id directly)
      const { data: enrollmentData } = await supabase
        .from('subscription_enrollments')
        .select('id')
        .eq('company_id', companyId);
      
      const enrollmentIds = enrollmentData?.map(e => e.id) || [];

      // Get current period revenue (use net_amount - what gym receives)
      const { data: currentPayments } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('amount, net_amount')
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'succeeded')
            .gte('billing_date', startDateStr)
            .lte('billing_date', endDateStr)
        : { data: [] };

      // Use net_amount if available (what gym receives), otherwise fall back to amount
      const revenue =
        currentPayments?.reduce((sum, p) => {
          const gymReceives = p.net_amount != null ? Number(p.net_amount) : Number(p.amount || 0);
          return sum + gymReceives;
        }, 0) || 0;

      // Get previous period revenue (use net_amount - what gym receives)
      const { data: previousPayments } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('amount, net_amount')
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'succeeded')
            .gte('billing_date', previousStartDateStr)
            .lte('billing_date', previousEndDateStr)
        : { data: [] };

      const previousRevenue =
        previousPayments?.reduce((sum, p) => {
          const gymReceives = p.net_amount != null ? Number(p.net_amount) : Number(p.amount || 0);
          return sum + gymReceives;
        }, 0) || 0;

      const growthPercent =
        previousRevenue > 0 ? ((revenue - previousRevenue) / previousRevenue) * 100 : 0;

      // Get subscription counts
      const { data: enrollments } = await supabase
        .from('subscription_enrollments')
        .select('id, status, created_at')
        .eq('company_id', companyId)
        .gte('created_at', startDateStr)
        .lte('created_at', endDateStr);

      const newSubscriptions = enrollments?.filter(e => e.status === 'active').length || 0;
      const cancellations =
        enrollments?.filter(e => e.status === 'cancelled').length || 0;

      // TODO: Calculate conversion rate and trials when trial system is implemented
      const conversionRate = 0;
      const trials = 0;

      return {
        revenue,
        growthPercent,
        conversionRate,
        newSubscriptions,
        cancellations,
        trials,
        period: reportPeriod,
        periodLabel,
      };
    },
  });

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      // TODO: Implement report generation and email functionality
      alert('Report generation coming soon!');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-white/80 mb-6">Owners only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-full bg-[#111010] pt-6">
        {/* Period Selector */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-[#EBFFEE]">Report Period:</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setReportPeriod('month')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    reportPeriod === 'month'
                      ? 'bg-[#9B1421] text-[#EBFFEE]'
                      : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setReportPeriod('year')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    reportPeriod === 'year'
                      ? 'bg-[#9B1421] text-[#EBFFEE]'
                      : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
                  }`}
                >
                  Year
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Metrics */}
        {isLoadingReport ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
            <span className="ml-3 text-[#EBFFEE]">Loading report...</span>
          </div>
        ) : reportData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <p className="text-sm text-[#A9A3A3] mb-1">Revenue</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">
                  ${reportData.revenue.toFixed(2)}
                </p>
                <p className="text-xs text-[#A9A3A3] mt-1">{reportData.periodLabel}</p>
              </CardContent>
            </Card>

            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <p className="text-sm text-[#A9A3A3] mb-1">Growth</p>
                <p
                  className={`text-2xl font-bold ${
                    reportData.growthPercent >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {reportData.growthPercent >= 0 ? '+' : ''}
                  {reportData.growthPercent.toFixed(1)}%
                </p>
                <p className="text-xs text-[#A9A3A3] mt-1">vs previous period</p>
              </CardContent>
            </Card>

            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <p className="text-sm text-[#A9A3A3] mb-1">New Subscriptions</p>
                <p
                  className={`text-2xl font-bold ${
                    reportData.newSubscriptions > 0 ? 'text-green-400' : 'text-[#EBFFEE]'
                  }`}
                >
                  {reportData.newSubscriptions}
                </p>
                <p className="text-xs text-[#A9A3A3] mt-1">{reportData.periodLabel}</p>
              </CardContent>
            </Card>

            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <p className="text-sm text-[#A9A3A3] mb-1">Cancellations</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">{reportData.cancellations}</p>
                <p className="text-xs text-[#A9A3A3] mt-1">{reportData.periodLabel}</p>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Actions */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-8">
            <CardTitle className="text-[#EBFFEE]">Report Actions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
              {isGenerating ? 'Generating...' : 'Generate Report'}
            </Button>
            <p className="text-sm text-[#A9A3A3] text-center">
              Report generation and email functionality coming soon
            </p>
          </CardContent>
        </Card>
      </div>
  );
}
